// Monetisation → Events (contracts/revenue.md §4 events, §5): every App Store notification and Stripe event that
// changed a subscription, newest first, filtered by group (charges, trials, churn) or one kind, store,
// environment and range, each row linking to its player's subscription page. "Load more" pages through it.

import { kindQuery, useRevenueEvents } from "../../api/revenue";
import type { RevenueEvent } from "../../api/revenueSchemas";
import { tableStyles } from "../../components/table";
import { Button, EmptyState, ErrorState, Loading, PageHeader, Segmented, Select } from "../../components/ui";
import { formatDateTime, formatShortDate } from "../../lib/dates";
import { EVENT_KINDS, KIND_GROUP_LABEL, KIND_GROUPS, kindMeta, productShort, scopeText, STORE_LABEL, type KindGroup } from "../../lib/revenue";
import pages from "../pages.module.css";
import { EstimateNote, KindChip, MoneyCell, PlayerCell, RangeControls, ResetFilters, SandboxBadge, ScopeControls, Storefront, useParamSetter, useRange, useScope } from "./components";
import r from "./revenue.module.css";

const isGroup = (v: string | null): v is KindGroup => (KIND_GROUPS as readonly string[]).includes(v ?? "");

export function EventsPage() {
  const range = useRange();
  const scope = useScope();
  const [params, set] = useParamSetter();
  const rawGroup = params.get("group");
  const group: KindGroup = isGroup(rawGroup) ? rawGroup : "all";
  const kind = params.get("kind") || null;
  const q = useRevenueEvents({ from: range.from, to: range.to, env: scope.env, store: scope.store, kind: kindQuery(group, kind) });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHeader
        title="Events"
        meta={
          <span>
            What the stores told us, newest first: App Store Server Notifications and Stripe webhook events, {formatShortDate(range.from)} –{" "}
            {formatShortDate(range.to)} (UTC) · {scopeText(scope.env, scope.store)}. A charge's time is its purchase date; a refund's, its revocation
            date.
          </span>
        }
        actions={
          <>
            <RangeControls range={range} />
            <ResetFilters />
          </>
        }
      />
      <div className={r.toolbar}>
        <Segmented
          label="Kind group"
          value={kind ? ("" as KindGroup) : group}
          options={KIND_GROUPS.map((g) => ({ value: g, label: KIND_GROUP_LABEL[g] }))}
          onChange={(v) => set({ group: v === "all" ? null : v, kind: null })}
        />
        <Select value={kind ?? ""} onChange={(e) => set({ kind: e.target.value || null, group: null })} aria-label="Kind">
          <option value="">Every kind</option>
          {EVENT_KINDS.map((k) => (
            <option key={k} value={k}>
              {kindMeta(k).label}
            </option>
          ))}
        </Select>
        <ScopeControls scope={scope} />
      </div>

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.isPending ? (
        <Loading rows={10} />
      ) : items.length === 0 ? (
        <EmptyState title="No events">Nothing of this kind happened in this range. Try another group, the All environment or a longer range.</EmptyState>
      ) : (
        <div className={tableStyles.wrap} aria-busy={q.isFetching && !q.isFetchingNextPage}>
          <table className={tableStyles.table} data-testid="events">
            <thead>
              <tr>
                <th>Time</th>
                <th>Kind</th>
                <th>Customer</th>
                <th>Product</th>
                <th data-align="right">Price</th>
                <th>Storefront</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <EventRow key={e.id} e={e} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.hasNextPage ? (
        <div className={pages.loadMore}>
          <Button size="sm" onClick={() => void q.fetchNextPage()} busy={q.isFetchingNextPage} data-testid="revenue-events-more">
            Load more
          </Button>
        </div>
      ) : null}
      {items.length ? (
        <p className={r.faintLine} data-testid="events-count">
          {items.length} shown{q.hasNextPage ? ", more below" : ""}
        </p>
      ) : null}
      <EstimateNote />
    </>
  );
}

function EventRow({ e }: { e: RevenueEvent }) {
  return (
    <tr data-testid="revenue-event-row" data-kind={e.kind}>
      <td className="mono">{formatDateTime(e.at)}</td>
      <td>
        <KindChip kind={e.kind} detail={e.detail} />
      </td>
      <td>
        <PlayerCell userId={e.userId} handle={e.handle} />
      </td>
      <td>{e.productId || e.plan ? productShort(e.productId, e.plan) : <span className={pages.faint}>—</span>}</td>
      <td data-align="right">
        <MoneyCell price={e.price} usd={e.usd} align="right" />
      </td>
      <td>
        <Storefront code={e.storefront} />
      </td>
      <td>
        <span className={r.storeCell}>
          <span>{STORE_LABEL[e.store] ?? e.store}</span>
          <SandboxBadge env={e.environment} />
        </span>
        <span className={r.detail}>
          {e.detail ? e.detail.replace(/_/g, " ") : null}
          <span className={r.raw}>{e.raw ?? e.kind}</span>
        </span>
      </td>
    </tr>
  );
}
